import { blinkConstants } from '@thoughtspot/blink-constants';
import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import { getCurrentOrgId, isOrgUiEnabled } from '@thoughtspot/session-service';
import { getClientId, isOktaEnabled } from '@thoughtspot/system-config-service';
import React, { useCallback, useEffect, useMemo, useState, } from 'react';
import { SamlConnectionListQuery } from "../../../services/saml/saml-service";
import { useGetLdapAdIamV1Query, useGetLocalIamV1Query, useGetSamlIamV1Query, useSetLocalIamV1Mutation, } from "../../../services/system/system-service";
import { BasePageContext } from "../../../contexts/basepage-context";
import { useGetUserAccessibleOrgsLazyQuery } from "../../../services/session/session-service";
import { getDefaultSamlHeaders, getSubDomainIfOrgEnabled, } from '../../saml/constant';
import { Content, Header, WithQueryHandler, } from '../../system/common';
import { ActionButtons, LearnMore, Mode, TranslationsKeys as TranslationsKeysCommon, } from '../../system/common/constants';
import { getConfig } from '../../system/common/form-builder-config';
import { getFormFields, parseFormData } from '../../system/common/utils';
import { TranslationsKeys } from '../constants';
import styles from './internal-auth.module.scss';
const pageName = 'internalAuth';
export const InternalAuthContainer = ({ showLoader, hideLoader, onSuccess, onError, }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState(Mode.VIEW);
    const initialInternalAuthConfig = useMemo(() => getConfig(pageName), []);
    const [internalAuthData, setInternalAuthData] = useState({});
    const [formFields, setFormFields] = useState([]);
    const [internalAuthPostData, setInternalAuthPostData] = useState(null);
    const [currentOrgInfo, setCurrentOrgInfo] = useState(null);
    const { metricsService } = BasePageContext();
    const statusConstants = {
        ACTIVE: 'ACTIVE',
        INACTIVE: 'INACTIVE',
    };
    const clientId = getClientId();
    const { data: oidcV2Response, loading: oidcV2Loading, error: oidcV2Error, } = SamlConnectionListQuery({
        clientId,
        isSaml: false,
        defaultHeaders: getDefaultSamlHeaders(),
    });
    const { data: samlV2Response, loading: samlV2Loading, error: samlV2Error, } = SamlConnectionListQuery({
        clientId,
        isSaml: true,
        defaultHeaders: getDefaultSamlHeaders(),
    });
    const { data: samlV1Response, loading: samlV1Loading, error: samlV1Error, } = useGetSamlIamV1Query();
    const { data: ldapAdResponse, loading: ldapAdLoading, error: ldapAdError, } = useGetLdapAdIamV1Query();
    const { error: orgError, data: orgsList, loading: orgsLoading, getUserAccessibleOrgsLazy, } = useGetUserAccessibleOrgsLazyQuery();
    useEffect(() => {
        if (isOrgUiEnabled()) {
            getUserAccessibleOrgsLazy();
        }
    }, []);
    useEffect(() => {
        if (orgsList) {
            const orgsData = orgsList?.Data?.orgs || [];
            const selectedOrg = orgsData.filter((org) => org.orgId === getCurrentOrgId().toString());
            const org = selectedOrg.length === 0 ? null : selectedOrg[0];
            setCurrentOrgInfo(org);
        }
    }, [orgsList]);
    const shouldFilterBasedOnSubdomain = () => {
        return !!(currentOrgInfo &&
            currentOrgInfo.orgName &&
            currentOrgInfo.orgId !== blinkConstants.orgs.MASTER_ID.toString());
    };
    const areIDPconnectionsEnabled = useCallback(() => {
        const connections = (samlV2Response?.Data.samlList || []).map(idp => ({
            ...idp,
            connectionName: idp.name,
        }));
        let newIdpList = [
            ...connections,
            ...(oidcV2Response?.Data.samlList || []),
        ];
        let subDomain = getSubDomainIfOrgEnabled();
        if (!subDomain) {
            subDomain = shouldFilterBasedOnSubdomain()
                ? `${currentOrgInfo.orgName}_`
                : '';
        }
        newIdpList = newIdpList.filter((saml) => saml.connectionName.startsWith(subDomain));
        let enabledCount = 0;
        newIdpList.forEach(idp => {
            if (idp.status === statusConstants.ACTIVE) {
                enabledCount += 1;
            }
        });
        return enabledCount > 0;
    }, [samlV2Response, oidcV2Response]);
    const { data: internalAuthResponse, loading: isInternalAuthLoading, error: internalAuthError, } = useGetLocalIamV1Query();
    useEffect(() => {
        if (isOktaEnabled()) {
            if (samlV2Loading ||
                oidcV2Loading ||
                isInternalAuthLoading ||
                orgsLoading) {
                showLoader();
                return;
            }
            hideLoader();
            if (samlV2Error || oidcV2Error || orgError) {
                onError(t(TranslationsKeys.INTERNEL_AUTH_CONFIG_LOAD_ERROR));
            }
        }
        else {
            if (samlV1Loading || ldapAdLoading || isInternalAuthLoading) {
                showLoader();
                return;
            }
            hideLoader();
            if (samlV1Error || ldapAdError) {
                onError(t(TranslationsKeys.INTERNEL_AUTH_CONFIG_LOAD_ERROR));
            }
        }
    }, [
        samlV1Loading,
        ldapAdLoading,
        samlV2Loading,
        oidcV2Loading,
        samlV1Error,
        samlV2Error,
        oidcV2Error,
        ldapAdError,
        isInternalAuthLoading,
        orgsLoading,
        orgError,
    ]);
    const getFieldConfig = (field) => {
        switch (field.key) {
            default:
                return field.config;
        }
    };
    const getAdditionlFieldAttribute = (field) => {
        switch (field.key) {
            case 'status':
                if (mode === Mode.EDIT) {
                    return {
                        true: t(TranslationsKeys.ENABLE),
                        false: t(TranslationsKeys.DISABLE),
                    };
                }
                return {
                    true: t(TranslationsKeys.ENABLED),
                    false: t(TranslationsKeys.DISABLED),
                };
            default:
                return null;
        }
    };
    const updateFormFields = (formData = {}, errorMessages = {}) => {
        const fieldsToShow = [];
        initialInternalAuthConfig.forEach((config) => {
            const newConfig = config;
            newConfig.valueLabels = getAdditionlFieldAttribute(config);
            if (config.key === 'status') {
                const oldConfig = newConfig.config;
                newConfig.config = {
                    ...oldConfig,
                    label: mode === Mode.EDIT
                        ? t(TranslationsKeysCommon.INTERNAL_AUTH_HEADING)
                        : t(TranslationsKeysCommon.INTERNAL_AUTH_STATUS),
                };
            }
            fieldsToShow.push({
                ...config,
                configCallback: getFieldConfig,
            });
        });
        const newFormFields = getFormFields(formData, fieldsToShow, errorMessages, mode);
        setFormFields(newFormFields);
    };
    const fetchInternalAuth = () => {
        if (internalAuthResponse &&
            !isInternalAuthLoading &&
            internalAuthResponse.Data) {
            setInternalAuthData(internalAuthResponse.Data);
        }
        else if (internalAuthResponse &&
            !isInternalAuthLoading &&
            internalAuthResponse.Error) {
            onError(t(TranslationsKeys.MESSAGE_API_FAILURE));
        }
    };
    useEffect(() => {
        showLoader();
    }, []);
    useEffect(() => {
        fetchInternalAuth();
    }, [internalAuthResponse]);
    useEffect(() => {
        updateFormFields(internalAuthData);
    }, [internalAuthData, mode]);
    const isSamlAdConfigured = useCallback(() => {
        const isAdConfigured = ldapAdResponse?.Data?.enable_ldap === true || false;
        const isSamlConfigured = samlV1Response?.Data.enable_saml === true || false;
        return isAdConfigured || isSamlConfigured;
    }, [ldapAdResponse, samlV1Response]);
    const canDisableInternalAuth = () => {
        if (isOktaEnabled()) {
            return areIDPconnectionsEnabled();
        }
        return isSamlAdConfigured();
    };
    const onSave = (currentFormData) => {
        const postData = { ...parseFormData(currentFormData) };
        if (!postData.status && !canDisableInternalAuth()) {
            if (isOktaEnabled()) {
                onError(t(TranslationsKeys.INTERNAL_AUTH_ERROR_OIDC_SAML_NOT_CONFIGURED));
                return;
            }
            onError(t(TranslationsKeys.INTERNAL_AUTH_ERROR_AD_SAML_NOT_CONFIGURED));
            return;
        }
        setInternalAuthPostData({ ...postData });
    };
    const onSaveSuccess = (response) => {
        setInternalAuthData(response.Data || {});
        metricsService.collectEvent(new UserAction(userActionTypeEnum.CONFIGURE_INTERNAL_AUTH_IAM_V1), {});
        setMode(Mode.VIEW);
        onSuccess(t(TranslationsKeys.TOAST_CHANGES_SAVED));
    };
    const onSaveError = (error) => {
        hideLoader();
        onError(t('admin.error.generic'));
    };
    const [updateInternalAuth] = useSetLocalIamV1Mutation(response => onSaveSuccess(response), onSaveError, internalAuthPostData);
    useEffect(() => {
        if (internalAuthPostData) {
            showLoader();
            updateInternalAuth();
        }
    }, [internalAuthPostData]);
    const actionHandler = (type, currentFormData = {}) => {
        switch (type) {
            case ActionButtons.EDIT:
                setMode(Mode.EDIT);
                break;
            case ActionButtons.BACK:
            case ActionButtons.CANCEL:
                setMode(Mode.VIEW);
                break;
            case ActionButtons.SAVE:
                onSave(currentFormData);
                break;
            default:
                setMode(Mode.EDIT);
                break;
        }
    };
    const headerActions = [
        {
            type: ActionButtons.EDIT,
            title: t(TranslationsKeys.SAML_MODAL_EDIT),
        },
    ];
    const renderContent = () => {
        const config = {
            title: mode === Mode.EDIT
                ? `${t(TranslationsKeys.SAML_MODAL_EDIT)} ${t(TranslationsKeysCommon.INTERNAL_AUTH_HEADING)}`
                : '',
            formFields,
        };
        const actions = {
            primary: {
                type: ActionButtons.SAVE,
                text: t(TranslationsKeys.SAVE),
                returnData: true,
            },
            secondary: {
                type: ActionButtons.CANCEL,
                text: t(TranslationsKeys.CANCEL),
            },
        };
        const internalAuthBannerAlertProps = {
            isVisible: mode === Mode.EDIT,
            type: BannerAlertType.WARNING,
            className: styles.authBanner,
            message: t(TranslationsKeys.INTERNAL_AUTH_FORM_CONTENT),
            showCloseButton: false,
            global: false,
        };
        return (React.createElement(Content, { key: `${pageName}_${mode}`, config: config, mode: mode, pageName: pageName, backConfirm: true, actions: actions, banner: internalAuthBannerAlertProps, actionHandler: actionHandler }));
    };
    return (React.createElement(React.Fragment, null,
        mode === Mode.VIEW && (React.createElement("div", { className: styles.internalAuthContainer },
            React.createElement(Header, { title: t(TranslationsKeysCommon.INTERNAL_AUTH_HEADING), subTitle: t(TranslationsKeys.INTERNAL_AUTH_CONFIGURED_HEADING), titleSize: "large", learnMoreKey: LearnMore.INTERNAL_AUTH, mode: mode, actions: mode === Mode.VIEW ? headerActions : undefined, actionHandler: actionHandler }))),
        React.createElement("div", { className: styles.internalAuthContainer, "data-testid": "internal_auth-container" }, renderContent())));
};
export const InternalAuth = () => (React.createElement(WithQueryHandler, null,
    React.createElement(InternalAuthContainer, null)));
//# sourceMappingURL=internal-auth.container.js.map